// ============================================================================
// AgcCalendar — shared room booking grid for AGC + UIF.
// ============================================================================
// FullCalendar timeGridWeek by default. Loads /api/calendar/bookings (which
// honors AGC redaction server-side) and /api/calendar/resources. Bookings
// appear colored by source:
//   * AGC (own org) — orange
//   * UIF — green (or gray when redacted)
//   * Blackout — red (regardless of source)
//   * Proposed — yellow striping via outline
//
// Click an empty slot → opens BookingFormModal in create mode.
// Click an existing booking → opens BookingFormModal in edit mode if the
// viewer can mutate it (UIF on anything, AGC on AGC-source rows), else
// in read-only mode with a back-out Close button.
//
// Resource filter at the top narrows the view to one room. Defaults to All.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { LogOut, Plus, RefreshCw } from 'lucide-react';
import { api, type BookingRow, type SharedResource } from '../api';
import { useAuth } from '../auth';
import { BookingFormModal } from './BookingFormModal';

// Color triplet (background, border, text) by booking_source + status.
// Picks return CSS color strings consumed by FullCalendar.
function colorFor(b: BookingRow, viewerRole: string | undefined): { bg: string; border: string; text: string } {
  if (b.booking_status === 'blackout') return { bg: '#7c2d12', border: '#5c1f0a', text: '#ffffff' };
  if (b.booking_status === 'proposed') return { bg: '#fbbf24', border: '#b45309', text: '#1f2937' };
  // Confirmed
  if (b.booking_source === 'agc') return { bg: '#D85A30', border: '#993C1D', text: '#ffffff' };
  if (b.booking_source === 'uif') {
    // AGC viewers see UIF bookings dimmed gray (redacted on server too)
    return viewerRole === 'agc'
      ? { bg: '#9CA3AF', border: '#6B7280', text: '#ffffff' }
      : { bg: '#1D9E75', border: '#0F6E56', text: '#ffffff' };
  }
  return { bg: '#6B7280', border: '#4B5563', text: '#ffffff' };
}

// Title to show on the event block. AGC sees redacted titles for UIF bookings.
// Task #46: proposed bookings get a "Draft · " prefix so admins can see at a
// glance that the slot is tentatively held (the linked UIF event is in draft
// mode and could be unpublished).
function titleFor(b: BookingRow, viewerRole: string | undefined): string {
  if (b.booking_status === 'blackout') return `${b.resource_name} — Blackout`;
  const prefix = b.booking_status === 'proposed' ? 'Draft · ' : '';
  if (viewerRole === 'agc' && b.booking_source !== 'agc') {
    return `${prefix}${b.resource_name} — Booked (tentative)`;
  }
  const body = b.event_title ?? b.notes ?? `${b.resource_name} — ${b.booking_source === 'agc' ? 'AGC' : 'UIF'} booking`;
  return prefix + body;
}

export function AgcCalendar() {
  const { user, token, logout } = useAuth();
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [resources, setResources] = useState<SharedResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [resourceFilter, setResourceFilter] = useState<number | 'all'>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BookingRow | null>(null);
  const [draft, setDraft] = useState<{ resourceId: number | null; start: string; end: string } | null>(null);

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError('');
    Promise.all([
      api.listBookings(token),
      api.sharedResources(token),
    ])
      .then(([b, r]) => {
        setBookings(b.bookings ?? []);
        setResources(r.resources ?? []);
      })
      .catch((err) => setError(err?.message || 'Could not load room calendar.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  // Filter out staff resources everywhere on this view — this is the shared
  // ROOM calendar, and staff bookings (created by hybrid event assignments)
  // don't belong here. The services-catalog view (future Task #F) is where
  // staff bookings will live.
  const roomResources = useMemo(
    () => resources.filter((r) => r.resource_type !== 'staff'),
    [resources],
  );
  const staffResourceIds = useMemo(
    () => new Set(resources.filter((r) => r.resource_type === 'staff').map((r) => r.resource_id)),
    [resources],
  );

  const filtered = useMemo(() => {
    let list = bookings.filter((b) => !staffResourceIds.has(b.resource_id));
    if (resourceFilter !== 'all') {
      list = list.filter((b) => b.resource_id === resourceFilter);
    }
    return list;
  }, [bookings, resourceFilter, staffResourceIds]);

  const fcEvents = useMemo(
    () =>
      filtered.map((b) => {
        const c = colorFor(b, user?.role);
        return {
          id: String(b.booking_id),
          title: titleFor(b, user?.role),
          start: b.start,
          end: b.end,
          backgroundColor: c.bg,
          borderColor: c.border,
          textColor: c.text,
          extendedProps: { booking: b },
        };
      }),
    [filtered, user?.role],
  );

  const canMutate = (b: BookingRow): boolean => {
    if (user?.role === 'uif') return true;
    if (user?.role === 'agc' && b.booking_source === 'agc') return true;
    return false;
  };

  const handleSelect = (info: { startStr: string; endStr: string }) => {
    setEditing(null);
    setDraft({
      resourceId: resourceFilter === 'all' ? (roomResources[0]?.resource_id ?? null) : resourceFilter,
      start: info.startStr,
      end: info.endStr,
    });
    setModalOpen(true);
  };

  const handleEventClick = (info: { event: { extendedProps: any } }) => {
    const b: BookingRow = info.event.extendedProps.booking;
    setEditing(b);
    setDraft(null);
    setModalOpen(true);
  };

  const handleLogout = () => {
    logout();
    window.location.hash = '';
  };

  const isAgc = user?.role === 'agc';
  const isUif = user?.role === 'uif';

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl font-bold text-uif-green-dark">Shared Room Calendar</h1>
            <p className="text-xs text-gray-500 -mt-0.5">
              Signed in as {user?.display_name ?? user?.username} · {user?.role?.toUpperCase()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
              Refresh
            </button>
            {(isAgc || isUif) && roomResources.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setDraft({
                    resourceId: resourceFilter === 'all' ? roomResources[0].resource_id : resourceFilter,
                    start: '',
                    end: '',
                  });
                  setModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white px-3 py-1.5 text-sm font-semibold hover:bg-uif-green-dark"
              >
                <Plus className="h-4 w-4" />
                Reserve room
              </button>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Room</span>
              <button
                type="button"
                onClick={() => setResourceFilter('all')}
                className={
                  'rounded-full px-3 py-1 text-xs border transition-colors ' +
                  (resourceFilter === 'all'
                    ? 'bg-uif-green text-white border-uif-green-dark'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                }
              >
                All rooms
              </button>
              {roomResources.map((r) => (
                <button
                  key={r.resource_id}
                  type="button"
                  onClick={() => setResourceFilter(r.resource_id)}
                  className={
                    'rounded-full px-3 py-1 text-xs border transition-colors ' +
                    (resourceFilter === r.resource_id
                      ? 'bg-uif-green text-white border-uif-green-dark'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                  }
                >
                  {r.resource_name}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden style={{ background: '#D85A30' }} className="inline-block w-3 h-3 rounded-sm" />
                AGC
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden style={{ background: isAgc ? '#9CA3AF' : '#1D9E75' }} className="inline-block w-3 h-3 rounded-sm" />
                UIF
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden style={{ background: '#fbbf24' }} className="inline-block w-3 h-3 rounded-sm" />
                Proposed
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden style={{ background: '#7c2d12' }} className="inline-block w-3 h-3 rounded-sm" />
                Blackout
              </span>
            </div>
          </div>

          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading calendar…</p>
          ) : (
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              timeZone="UTC"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,listWeek',
              }}
              buttonText={{ today: 'Today', month: 'Month', week: 'Week', list: 'List' }}
              events={fcEvents}
              selectable={isAgc || isUif}
              select={(info) => handleSelect({ startStr: info.startStr, endStr: info.endStr })}
              eventClick={(info) => handleEventClick({ event: { extendedProps: info.event.extendedProps } })}
              height="auto"
              firstDay={0}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
              nowIndicator
            />
          )}
        </div>

        {roomResources.length === 0 && !loading && (
          <p className="text-center text-sm text-gray-500">
            No shared rooms configured yet. Ask UIF to add a resource.
          </p>
        )}
      </main>

      {token && (
        <BookingFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
          token={token}
          resources={roomResources}
          bookingId={editing?.booking_id ?? null}
          initialResourceId={editing?.resource_id ?? draft?.resourceId ?? null}
          initialStart={editing?.start ?? draft?.start ?? null}
          initialEnd={editing?.end ?? draft?.end ?? null}
          initialNotes={editing?.notes ?? null}
          initialStatus={editing?.booking_status ?? 'confirmed'}
          canDelete={editing != null && canMutate(editing)}
          // For create mode (editing==null), the user is making their own booking
          // so canEdit=true. For edit mode, only the booking's owning role (or
          // UIF as admin) can mutate; everyone else gets read-only.
          canEdit={editing == null || canMutate(editing)}
        />
      )}
    </div>
  );
}
